import { z } from 'zod'
import { canonicalCountry, canonicalLanguage } from './coverage-vocab'

/**
 * Coverage — which outlets, countries and languages a report's content may come from.
 * Topics decide what it's about; coverage decides where it can come from.
 *
 * Lists are the shared library (account-scoped, referenced LIVE — an edit moves every report
 * pointing at it). Rules are the applications: a report or one of its headers applies a list
 * or an ad-hoc value to one field. Resolution (inheritance, flattening to values) is
 * `effectiveCoverage` in @headline/db — the one resolver every consumer shares.
 */

export const coverageListTypeSchema = z.enum(['outlets', 'countries', 'languages', 'priority'])
export type CoverageListType = z.infer<typeof coverageListTypeSchema>

export const coverageFieldSchema = z.enum([
  'outlets.include',
  'outlets.exclude',
  'countries.include',
  'countries.exclude',
  'languages.include',
  'languages.exclude',
])
export type CoverageField = z.infer<typeof coverageFieldSchema>

/** One member value: a registrable domain, an ISO country code, or an ISO language code.
 *  Soft-closed vocabulary — the catalog assists, free entry stays allowed (outlets). */
const memberValueSchema = z.string().trim().min(1).max(255)

export const coverageListSchema = z.object({
  id: z.uuid(),
  /** Null = global built-in (internal-admin managed). */
  accountId: z.uuid().nullable(),
  type: coverageListTypeSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(500),
  /** Built-in behavior key ('global-blocklist'); null for ordinary lists. */
  role: z.string().nullable(),
  /** Ordered — order is load-bearing for 'priority' lists, cosmetic for the rest. */
  members: z.array(memberValueSchema),
  /** How many rules reference this list — the blast radius shown before edits and deletes. */
  usedIn: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type CoverageList = z.infer<typeof coverageListSchema>

export const createCoverageListSchema = z.object({
  /**
   * The owning account, or null for a GLOBAL list available to every account (the query unions
   * `accountId = $account OR accountId IS NULL`). Null here is a user-created global — distinct
   * from a seeded built-in, which additionally carries a `role`. Present-but-nullable rather than
   * optional, so a caller can never create an account list by forgetting the field.
   */
  accountId: z.uuid().nullable(),
  type: coverageListTypeSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(500).default(''),
  members: z.array(memberValueSchema).min(1),
})
export type CreateCoverageList = z.infer<typeof createCoverageListSchema>

export const updateCoverageListSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(500).optional(),
    /** Full replacement, in order — the edit dialog always submits the whole membership. */
    members: z.array(memberValueSchema).min(1).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'Nothing to update.' })
export type UpdateCoverageList = z.infer<typeof updateCoverageListSchema>

/**
 * Outlets a report scoped to the United States receives anyway, whatever country the story was
 * filed from.
 *
 * The warehouse records where a piece was filed, so Reuters reads GB and the Daily Mail reads GB,
 * and a US-scoped header that took the country literally would drop the wires — which is not what
 * a US media report means by coverage. The rule is fleet-wide and deliberately short: an outlet
 * belongs here only if a US report would be wrong to omit it. One client wanting one more outlet
 * is a coverage rule on their own report.
 *
 * `fn_us_coverage_outlets()` in clipbook-data-schema is where this is ENFORCED, for both the topic
 * path and the legacy header path. This copy exists so the coverage editor can tell a client which
 * outlets those are; keep the two in step (the migration says so on its side too).
 */
export const US_COVERAGE_OUTLETS: readonly string[] = [
  'reuters.com',
  'bbc.com',
  'bbc.co.uk',
  'theguardian.com',
  'independent.co.uk',
  'the-independent.com',
  'ft.com',
  'telegraph.co.uk',
  'economist.com',
  'afp.com',
  'abcnews.com',
  'dailymail.co.uk',
  'mirror.co.uk',
  'thesun.co.uk',
  'dailystar.co.uk',
  'dailyrecord.co.uk',
]

/**
 * Whether one article is allowed through by a resolved coverage map — the ONE place the rules are
 * interpreted, so the warehouse pull and the data-API pull can never disagree about what
 * "exclude nytimes.com" means.
 *
 * Per axis: a non-empty `include` is a whitelist (anything not on it is out); `exclude` always
 * wins over `include`, which is what makes the global blocklist unanswerable. An axis the article
 * carries no value for is NOT filtered — a hit with no language tag is the collector's silence,
 * and dropping coverage on silence would quietly shrink a report nobody asked to shrink.
 */
/**
 * The rows an OUTLET rule is about: web coverage. A broadcast clip, a podcast episode or a social
 * post has no outlet in the rules' sense — its `domain` is the host it was fetched from (TVEyes,
 * Apple, x.com) — so an include list of news outlets must not drop every one of them (CLI-758).
 */
export const OUTLET_RULED_MEDIA = ['news', 'newsletter'] as const
export type CoverageMediaType = 'news' | 'newsletter' | 'broadcast' | 'podcast' | 'social'

export function coverageAllows(
  article: {
    domain?: string | null
    language?: string | null
    country?: string | null
    /**
     * When given, the outlet axis judges only web coverage (`OUTLET_RULED_MEDIA`). Absent, every
     * row is judged on its domain — the RTA runner's and the report pull's reading, unchanged.
     */
    mediaType?: CoverageMediaType | null
  },
  coverage: Partial<Record<CoverageField, string[]>>,
): boolean {
  const outletRuled =
    article.mediaType == null ||
    (OUTLET_RULED_MEDIA as readonly string[]).includes(article.mediaType)
  return (
    (!outletRuled ||
      axisAllows(
        article.domain,
        coverage['outlets.include'],
        coverage['outlets.exclude'],
        domainMatches,
      )) &&
    axisAllows(
      article.language,
      coverage['languages.include'],
      coverage['languages.exclude'],
      languageMatches,
    ) &&
    axisAllows(
      article.country,
      coverage['countries.include'],
      coverage['countries.exclude'],
      countryMatches,
    )
  )
}

/**
 * The rules still ours to apply once the data API has applied the rest. A topic request carries
 * the include axes and both outlet lists, and the warehouse enforces them in legacy's own
 * vocabulary — Reuters, the BBC and a handful of global wires count as US coverage there — so
 * re-checking those axes here would drop stories the portal delivered for years (a US-only
 * header lost its Reuters story that way). Country and language EXCLUSIONS have no field on that
 * request, so they stay ours.
 */
export function coverageAfterDataApi(
  coverage: Partial<Record<CoverageField, string[]>>,
): Partial<Record<CoverageField, string[]>> {
  const residual: Partial<Record<CoverageField, string[]>> = {}
  if (coverage['countries.exclude']) residual['countries.exclude'] = coverage['countries.exclude']
  if (coverage['languages.exclude']) residual['languages.exclude'] = coverage['languages.exclude']
  return residual
}

function axisAllows(
  value: string | null | undefined,
  include: string[] | undefined,
  exclude: string[] | undefined,
  matches: (value: string, rule: string) => boolean,
): boolean {
  if (!value) return true
  if (exclude?.some((rule) => matches(value, rule))) return false
  if (include && include.length > 0) return include.some((rule) => matches(value, rule))
  return true
}

/**
 * A coverage outlet is a REGISTRABLE domain, so a rule covers its subdomains: excluding
 * `nytimes.com` has to catch `www.nytimes.com` and `cooking.nytimes.com`, which is plainly what
 * an associate means by it. Matching on equality alone would let the same outlet back in under
 * whichever host the collector happened to record.
 */
function domainMatches(value: string, rule: string): boolean {
  const host = normalizeOutletHost(value)
  const target = normalizeOutletRule(rule)
  if (target === '') return false
  return host === target || host.endsWith(`.${target}`)
}

/** A hit's domain as the outlet rules compare it: lower-case, no trailing dot. */
export function normalizeOutletHost(value: string): string {
  return value.trim().toLowerCase().replace(/\.$/, '')
}

/**
 * An outlet rule as `domainMatches` reads it: lower-case, no leading `*.` or `.`, no trailing
 * dot. Exported so a SQL prefilter (the RTA runner's poll, which must cap on rows that pass)
 * compares exactly what this function compares: equal, or ends with `.` + rule.
 */
export function normalizeOutletRule(rule: string): string {
  return rule
    .trim()
    .toLowerCase()
    .replace(/^\*?\.?/, '')
    .replace(/\.$/, '')
}

/**
 * A hit carries the ISO code (`US`, `en`, `pt-BR`); a rule may carry the code or the name the
 * editor offers ("United States", "English") — both sides canonicalise through the shared
 * vocabulary before comparing, so `pt-BR` answers a "Portuguese" rule and `US` answers
 * "United States". See coverage-vocab.ts for why this was ever otherwise.
 */
function languageMatches(value: string, rule: string): boolean {
  return canonicalLanguage(value) === canonicalLanguage(rule)
}

function countryMatches(value: string, rule: string): boolean {
  return canonicalCountry(value) === canonicalCountry(rule)
}

export const listCoverageListsQueryStringSchema = z.object({
  accountId: z.uuid(),
  type: coverageListTypeSchema.optional(),
})

export const listCoverageListsResponseSchema = z.object({
  lists: z.array(coverageListSchema),
})
export type ListCoverageListsResponse = z.infer<typeof listCoverageListsResponseSchema>

/**
 * One row of the internal Coverage List Library: every list in the org exactly once, with a member
 * COUNT instead of the members (the table shows only the count; the edit dialog fetches the full
 * list by id). One response for the whole library replaces the per-account fan-out (CLI-855).
 */
export const coverageLibraryEntrySchema = coverageListSchema
  .omit({ members: true })
  .extend({ memberCount: z.number().int().nonnegative() })
export type CoverageLibraryEntry = z.infer<typeof coverageLibraryEntrySchema>

export const coverageLibraryResponseSchema = z.object({
  lists: z.array(coverageLibraryEntrySchema),
})
export type CoverageLibraryResponse = z.infer<typeof coverageLibraryResponseSchema>

/**
 * Copy a coverage list into one or more scopes — each an accountId, or `null` for the shared
 * (global) library. Every target gets its OWN independent copy: members and type carried over, name
 * auto-suffixed on collision, and any built-in `role` dropped (a copy is an ordinary list, never a
 * second global behaviour). A clone, not a shared link.
 */
export const copyCoverageListSchema = z.object({
  targets: z.array(z.uuid().nullable()).min(1).max(50),
})
export type CopyCoverageList = z.infer<typeof copyCoverageListSchema>

export const copyCoverageListResponseSchema = z.object({ copied: z.number().int().nonnegative() })
export type CopyCoverageListResponse = z.infer<typeof copyCoverageListResponseSchema>

/**
 * One applied rule. Exactly one of listId/value — a reference chip or an ad-hoc chip. The
 * scope (report vs section) is carried by the route that accepts it, not repeated here.
 */
export const coverageRuleInputSchema = z
  .object({
    field: coverageFieldSchema,
    listId: z.uuid().nullish(),
    value: memberValueSchema.nullish(),
  })
  .refine((r) => (r.listId == null) !== (r.value == null), {
    message: 'Exactly one of listId or value.',
  })
export type CoverageRuleInput = z.infer<typeof coverageRuleInputSchema>

export const coverageRuleSchema = z.object({
  id: z.uuid(),
  field: coverageFieldSchema,
  listId: z.uuid().nullable(),
  value: z.string().nullable(),
})
export type CoverageRule = z.infer<typeof coverageRuleSchema>

/**
 * The resolved truth for one header: per field, the flat value set after list expansion and
 * inheritance (section rules for a field replace the report's; none = inherit). What report
 * generation filters by — and the shape webapp facets and RTA constraints will read later.
 */
export const effectiveCoverageSchema = z.object({
  reportId: z.uuid(),
  /** Report-level resolution, and one entry per section keyed by section id. */
  report: z.record(coverageFieldSchema, z.array(z.string())),
  sections: z.record(z.string(), z.record(coverageFieldSchema, z.array(z.string()))),
})
export type EffectiveCoverage = z.infer<typeof effectiveCoverageSchema>
